/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    textFieldValue1 = "textFieldValue1",
    textFieldValue2 = "textFieldValue2",
    textFieldValue3 = "textFieldValue3",
    imageLink = "imageLink",
    destLink = "destLink",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.textFieldValue1]: string;
        [fields.textFieldValue2]: string;
        [fields.textFieldValue3]: string;
        [fields.imageLink]: string;
        [fields.destLink]: string;
        [fields.openInNewWindow]: boolean;
    };
}

type Props = OwnProps;

export interface CustomContactSectionWidgetStyles {
    container?: InjectableCss;
    link?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    sectionHeadig?: InjectableCss;
    textField: InjectableCss;
    sectionContent?: InjectableCss;
}

const CustomContactSectionWidgetStyles: CustomContactSectionWidgetStyles = {
    container: {
        css: css`
            margin: 30px 0 30px;
            text-align: center;
            border-width: 1px;
            border-style: solid;
            border-color: #ebedf2;
            font-size: 14px;
            padding: 30px 0;
            min-height: 220px;
            height: 100%;
        `,
    },
    link: {
        typographyProps: {
            color: "text.main",
        },
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    sectionHeadig: {
        css: css`
            font-size: 17px;
            line-height: 24px;
            margin-bottom: 10px;
            color: #2450a8;
            &:hover {
                color: #2450a8;
            }
        `,
    },

    textField: {
        css: css`
            color: #2450a8;
            &:hover {
                color: #2450a8;
            }
        `,
    },
};

const CustomContactSectionWidget: React.FC<Props> = props => {
    return (
        <StyledWrapper {...CustomContactSectionWidgetStyles.container}>
            <Link
                href={props.fields.destLink ? props.fields.destLink : "{null}"}
                target={props.fields.openInNewWindow ? "_blank" : ""}
            >
                <StyledWrapper {...CustomContactSectionWidgetStyles.imageWrapper}>
                    <img src={props.fields.imageLink} alt="" />
                </StyledWrapper>
                <StyledWrapper {...CustomContactSectionWidgetStyles.sectionHeadig}>
                    {props.fields.textFieldValue1}
                </StyledWrapper>
                <StyledWrapper {...CustomContactSectionWidgetStyles.textField}>
                    {props.fields.textFieldValue2}
                </StyledWrapper>
                <StyledWrapper {...CustomContactSectionWidgetStyles.textField}>
                    {props.fields.textFieldValue3}
                </StyledWrapper>
            </Link>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Testing",
    fieldDefinitions: [
        {
            name: fields.textFieldValue1,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.textFieldValue2,
            displayName: "ContentLine1",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.textFieldValue3,
            displayName: "ContentLine2",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.imageLink,
            displayName: "Image Link",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.destLink,
            displayName: "Destination Link",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.openInNewWindow,
            displayName: "Open in new window",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: CustomContactSectionWidget,
    definition,
};

export default widgetModule;
